import React, { useEffect, useState } from "react";

import { Close } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import QuizIcon from "@mui/icons-material/Quiz";
import {
  Modal,
  Box,
  Typography,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
} from "@mui/material";

import SUGGESTED from "../../assets/icons/suggested.webp";
import QuestionCard from "../Assessments/QuestionCard";
import { getCamelCaseWord } from "../../utils/functions";

const style = {
  position: "absolute",
  top: "2%",
  left: "3%",
  width: { sm: "95vw", xs: "auto" },
  height: "96vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  outline: "none",
  borderRadius: "10px",
  overflowY: "auto",
  p: 2,
};

const AssessmentDetailsModal = ({ open, onClose, assessment }) => {
  const [tabValue, setTabValue] = React.useState(
    assessment?.tests[0]?.test_name
  );
  const [activeTab, setActiveTab] = useState("text");
  const [selectedTest, setSelectedTest] = useState("");
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  useEffect(() => {
    try {
      setTabValue(assessment?.tests[0]?.test_name);
      setActiveTab("text");
    } catch (error) {
      console.log(error);
    }
  }, [assessment?.tests?.length]);

  useEffect(() => {
    try {
      const foundTest = assessment?.tests?.find(
        (test) => test?.test_name === tabValue
      );
      setSelectedTest(foundTest);

      // Determine default activeTab based on available question types
      if (foundTest) {
        const hasTextQuestions = foundTest?.questions?.some(
          (q) => q?.question_category?.toLowerCase() === "text"
        );
        const hasMCQs = foundTest?.questions?.some(
          (q) => q?.question_category?.toLowerCase() === "multiple choice"
        );

        if (!hasTextQuestions && hasMCQs) {
          setActiveTab("multiple choice");
        } else {
          setActiveTab("text");
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [tabValue]);

  useEffect(() => {
    if (selectedTest) {
      const filtered = selectedTest?.questions?.filter(
        (question) => question?.question_category?.toLowerCase() === activeTab
      );
      setFilteredQuestions(filtered);
    }
  }, [activeTab, selectedTest]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!assessment) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <IconButton sx={{ float: "right" }} onClick={onClose}>
          <Close />
        </IconButton>
        <Box p={3} mt={2}>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {assessment?.assessment_name}
                </Typography>
                {assessment?.suggested && (
                  <Box
                    component="img"
                    src={SUGGESTED}
                    width={100}
                    height={25}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {assessment?.assessment_description}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                color: "text.secondary",
                mb: 3,
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" />
                <Typography variant="body2">
                  {assessment?.assessment_time}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <QuizIcon fontSize="small" />
                <Typography variant="body2">
                  {assessment?.questions_count} questions
                </Typography>
              </Box>
              <Typography variant="body2">
                {assessment?.questions_category
                  ?.filter(Boolean) // Remove undefined/null values
                  .slice(0, 2) // Take the first two elements
                  .map(getCamelCaseWord) // Convert them using your function
                  .join(" - ")}{" "}
                {/* Join with " - " */}
              </Typography>
            </Box>
          </Box>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: "black", // Set text color to black
                textTransform: "none", // Remove text transformation
              },
              "& .Mui-selected": {
                color: "black", // Ensure selected tab also has black color
                backgroundColor: "#4BC7E11A",
              },
            }}
          >
            {assessment?.tests?.map((tag, index) => (
              <Tab key={index} label={tag?.test_name} value={tag?.test_name} />
            ))}
          </Tabs>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: { md: "row", xs: "column" },
              height: "auto",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                my: 3,
                width: { md: "80%", xs: "100%" },
              }}
            >
              <Box
                sx={{
                  pr: 3,
                  width: "100%",
                }}
              >
                {selectedTest?.questions?.some(
                  (q) => q.question_category === "text"
                ) && (
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: activeTab === "text" ? "#4BC7E1" : "lightgrey",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 5,
                      mb: 1,
                      cursor: "pointer",
                    }}
                    onClick={() => handleChange(null, "text")}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      Text Based
                    </Typography>
                  </Box>
                )}

                {selectedTest?.questions?.some(
                  (q) => q?.question_category === "multiple choice"
                ) && (
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor:
                        activeTab === "multiple choice"
                          ? "#4BC7E1"
                          : "lightgrey",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 5,
                      mb: 2,
                      ml: selectedTest?.questions?.some(
                        (q) => q.question_category === "text"
                      )
                        ? 1
                        : 0,
                      cursor: "pointer",
                    }}
                    onClick={() => handleChange(null, "multiple choice")}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      Multiple Choice
                    </Typography>
                  </Box>
                )}

                {filteredQuestions?.map((question, index) => (
                  <QuestionCard
                    key={index}
                    question={question}
                    activeTab={activeTab}
                  />
                ))}
              </Box>
            </Box>

            <Box
              sx={{ width: { md: "20%", xs: "100%" }, borderRadius: 1, p: 2 }}
            >
              <Typography
                sx={{
                  mb: 1,
                  color: "#4BC7E1",
                  fontWeight: "bold",
                  ml: 3,
                  fontFamily: "Lato",
                  fontSize: "1.3rem",
                }}
              >
                Measurable Skills
              </Typography>
              <List dense>
                {selectedTest?.measureable_skills?.map((skill, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckIcon
                        color="primary"
                        fontSize="medium"
                        fontWeight="bold"
                      />
                    </ListItemIcon>
                    <ListItemText primary={skill?.toUpperCase()} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AssessmentDetailsModal;
